import { Link } from "react-router-dom";
import { LegalPage } from "@/components/LegalPage";
import { Placeholder } from "@/components/Placeholder";
import { BUSINESS, CONTACT } from "@/data/content";

const P = ({ children }) => <p className="mb-3 last:mb-0">{children}</p>;
const UL = ({ items }) => (
  <ul className="mb-3 list-disc space-y-1 pl-5 last:mb-0">
    {items.map((i) => <li key={i}>{i}</li>)}
  </ul>
);

const SECTIONS = [
  {
    heading: "Who We Are",
    body: (
      <P>
        This Privacy Policy explains how {CONTACT.name} (legal name: <Placeholder field={BUSINESS.legalName} />), of{" "}
        {CONTACT.address}, {CONTACT.country}, collects, uses and protects personal information when you use this website or our
        services. Contact: {CONTACT.email}, {CONTACT.phone}.
      </P>
    ),
  },
  {
    heading: "Information We Collect",
    body: (
      <>
        <P><strong>Contact form information.</strong> When you submit our contact / quote form we collect your name, email address, telephone number (optional), company name, the service you are interested in, your project requirements, budget range (optional), your message, and the date and time of submission.</P>
        <P><strong>Customer information.</strong> If you become a client we collect the information needed to deliver and invoice the services: contact details, business details, and the content, credentials and access you choose to share for the project.</P>
        <P><strong>Payment-related information.</strong> We do not take card payments on this website. Invoices are paid by the methods described on our Payment Information page. We record the fact and amount of payment for accounting purposes; we do not store card numbers.</P>
        <P><strong>Technical information.</strong> Our hosting provider records standard server logs (such as IP address, browser type and pages requested) for security and operational purposes.</P>
      </>
    ),
  },
  {
    heading: "How We Use Information",
    body: (
      <UL items={[
        "To respond to your enquiry and prepare a quotation.",
        "To deliver, manage and invoice the services you have engaged us for.",
        "To communicate with you about your project or ongoing service.",
        "To keep business and accounting records as required.",
        "To maintain the security and performance of this website.",
      ]} />
    ),
  },
  {
    heading: "Client Project Data",
    body: (
      <P>
        When a business engages us, we may receive business documents, website content, customer records and access
        credentials needed to fulfil the project. This data is treated as confidential, used only to perform the agreed work,
        stored in access-controlled systems, and never sold or shared for marketing. Credentials are returned, rotated or
        revoked at the end of the engagement. On written request, and at the end of an engagement, Client project data is
        returned or deleted except where we must retain records for accounting or legal reasons.
      </P>
    ),
  },
  {
    heading: "Service Providers and Processors",
    body: (
      <P>
        We use trusted third-party providers to operate our business, including website hosting, a database for storing form
        submissions, and a transactional email service that delivers a copy of each enquiry to {CONTACT.email}. These providers
        process information only on our instructions and for the purposes above. We do not sell, rent or trade personal information,
        and we do not share it with third parties for their own marketing.
      </P>
    ),
  },
  {
    heading: "Data Retention",
    body: (
      <P>
        Enquiry information is retained for 3 months after your last contact with us unless you become a client or ask us to delete it sooner.
        Client and accounting records are retained for as long as required by law and legitimate business needs, after which they are deleted or anonymised.
      </P>
    ),
  },
  {
    heading: "Security",
    body: (
      <P>
        This website is served over HTTPS. Form submissions are stored in an access-controlled database. Client credentials shared with us are used only for the agreed work and returned or revoked when the engagement ends. No method of transmission or storage is completely secure, but we take reasonable steps to protect your information.
      </P>
    ),
  },
  {
    heading: "Your Rights and Choices",
    body: (
      <P>
        You may ask us at any time what personal information we hold about you, ask us to correct it, or ask us to delete it where we are not required to keep it. To make a request, email {CONTACT.email} or write to the address above. We will respond within a reasonable time. Depending on where you live, you may have additional rights under local law; we will honour those rights as they apply.
      </P>
    ),
  },
  {
    heading: "Cookies",
    body: (
      <P>
        This website uses strictly necessary cookies and local storage to function (for example, to remember your cookie choice). It may also use privacy-conscious analytics to understand how the site is used. We do not use advertising or cross-site tracking cookies. See our{" "}
        <Link to="/cookie-policy" className="font-medium text-brand hover:underline">Cookie Policy</Link> for details.
      </P>
    ),
  },
  {
    heading: "Children",
    body: <P>Our services are directed at businesses. We do not knowingly collect personal information from children.</P>,
  },
  {
    heading: "Changes to This Policy",
    body: <P>We may update this policy from time to time. The current version is always available on this page with the date of last update.</P>,
  },
  {
    heading: "Contact",
    body: <P>Questions, access or deletion requests: {CONTACT.email} · {CONTACT.phone} · {CONTACT.name}, {CONTACT.address}, {CONTACT.country}.</P>,
  },
];

export default function PrivacyPolicy() {
  return (
    <LegalPage
      testId="privacy-page"
      overline="Legal — Privacy Policy"
      title="Privacy Policy"
      lede="What information we collect, how we use it, who processes it, how long we keep it and how you can ask us about it."
      seoDescription="Privacy Policy for My Geeks LLC: information collected via our contact form and services, how it is used, retention, security and your rights."
      sections={SECTIONS}
      updated="October 2, 2026"
    />
  );
}
